import { useEffect, useState } from "react";
import styled from "styled-components";
import { deleteMusicPlayer, updateMusicPlayer } from "../../scripts/musicPlayer";
import { ButtonRowContainer, ModalContainer, ModalHeader, ModalText } from "../panel/ModalStyles";
import MusicPlayerItem from "../panel/musicPlayer/MusicPlayerItem";
import Button from "../UiComponents/Button";
import Card from "../UiComponents/Card";
import Dropdown from "../UiComponents/Dropdown";
import InputField from "../UiComponents/InputField";
import Modal from "../UiComponents/Modal";
import theme from "../../UI/theme";
import VirtualDJSettings from "../panel/musicPlayer/VirtualDj/VirtualDjSettings";

const Container = styled.div`
    margin-bottom: 40px;
`

const SettingsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    border-bottom: 2px solid ${theme.colors.darkSpace80};
    margin-bottom: 20px;

    h2 {
        margin: 0;
    }
`

const MusicPlayerCard = styled(Card)`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 10px;
`

const RowContainer = styled.div`
    display: flex;
    > *:not(:last-child) {
        margin-right: 10px;
    }

    justify-content: space-between;
    align-items: center;
`

const WarnLabel = styled.label`
    color: ${props => props.$warn ? theme.colors.raspberryRed : 'inherit'};
`

const MusicPlayerSettings = props => {
    const [isEditing, setIsEditing] = useState(false);
    const [changesMade, setChangesMade] = useState(false);
    const [name, setName] = useState(props?.musicPlayer?.name ?? '');
    const [defaultVolume, setDefaultVolume] = useState(props?.musicPlayer?.defaultVolume);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [music, setMusic] = useState();

    const resolveConnectedSource = () => {
        let musicPlayerSource = props?.sources?.find(source => source?.sourceId === props?.musicPlayer?.sourceId);
        if (musicPlayerSource) {
            return ({
                ...musicPlayerSource,
                key: musicPlayerSource?.sourceId,
                label: musicPlayerSource?.name
            });
        } else {
            return null
        }
    }

    const [connectedSource, setConnectedSource] = useState(resolveConnectedSource());

    const onUpdateMusicPlayer = async () => {
        let pickedMusicPlayer = (({ musicPlayerId, customerId }) => ({ musicPlayerId, customerId }))(props.musicPlayer);
        await updateMusicPlayer({ ...pickedMusicPlayer, name: name, defaultVolume: defaultVolume, sourceId: connectedSource?.sourceId });
        setIsEditing(false);
        setChangesMade(false);
    }

    const onChangeName = (newValue) => {
        setChangesMade(true);
        setName(newValue);
    }

    const onChangeDefaultVolume = (newValue) => {
        let newDefaultVolume = Number(newValue);
        setChangesMade(true);
        setDefaultVolume(newDefaultVolume);
    }

    const onChangeSource = (newValue) => {
        setChangesMade(true);
        setConnectedSource(newValue);
    }

    const onCancelChanges = () => {
        setIsEditing(false);
        setName(props?.musicPlayer?.name ?? '');
        setDefaultVolume(props?.musicPlayer?.defaultVolume);
        setConnectedSource(resolveConnectedSource());
        setChangesMade(false);
    }

    return <Container>
        <MusicPlayerCard key={props?.musicPlayer?.musicPlayerId}>
            
            <MusicPlayerItem
                musicPlayer={props?.musicPlayer}
                onMusicChange={(music) => setMusic(music)}
                onAuthorizationChange={props.onAuthorizationChange}
                inSettingsView={true}
                hubs={props.hubs}
            />

            <SettingsContainer>
                <h2>Player Settings</h2>
                <RowContainer>
                    <label>Name</label>
                    {isEditing === true ? <InputField value={name} onChange={(e) => onChangeName(e.target.value)} /> : <label>{props?.musicPlayer?.name}</label>}
                </RowContainer>

                <RowContainer>
                    <label >Default volume</label>
                    {isEditing === true ?
                        <InputField value={defaultVolume} type="number" min={0} step={1} onChange={(e) => onChangeDefaultVolume(e.target.value)} />
                        :
                        <WarnLabel $warn={props?.musicPlayer.defaultVolume !== music?.volume}>{props?.musicPlayer?.defaultVolume ?? 'None'}</WarnLabel>}
                </RowContainer>

                <RowContainer>
                    <label>Current volume</label>
                    <WarnLabel $warn={music?.volume !== props?.musicPlayer?.defaultVolume}>{music?.volume}</WarnLabel>
                </RowContainer>

                <RowContainer>
                    <label>Connected source</label>
                    {isEditing === true ?
                        <Dropdown
                            items={props?.sources?.map(source => { return { ...source, label: source.name, key: source.sourceId } })}
                            selectedItem={connectedSource}
                            onClick={(item) => onChangeSource(item)} />
                        : <label>
                            {connectedSource?.name ?? 'None'}
                        </label>}
                </RowContainer>

                <ButtonRowContainer>
                    {isEditing === true ?
                        <Button secondary small onClick={() => onCancelChanges()}>Cancel</Button> :
                        <Button secondary small onClick={() => setIsEditing(true)}>Edit</Button>}
                    <Button tertiary small onClick={() => setShowDeleteModal(true)}> Delete</Button>
                    {isEditing === true && changesMade === true ?
                        <Button primary small onClick={() => onUpdateMusicPlayer()}>Save changes</Button>
                        : <></>}
                </ButtonRowContainer>
            </SettingsContainer>

            <VirtualDJSettings
                customerId={props.musicPlayer?.customerId}
                musicPlayerId={props.musicPlayer?.musicPlayerId}
                hubs={props.hubs}
                musicPlayer={props.musicPlayer}
            />


        </MusicPlayerCard>

        <Modal show={showDeleteModal} close={() => setShowDeleteModal(false)}>
            <ModalContainer>
                <ModalHeader>Delete music player?</ModalHeader>
                <ModalText>Are you sure you want to delete this music player?</ModalText>
                <ButtonRowContainer>
                    <Button secondary small onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button primary small onClick={() => deleteMusicPlayer(props?.musicPlayer?.musicPlayerId)}>Delete</Button>
                </ButtonRowContainer>
            </ModalContainer>
        </Modal >
    </Container>
}

export default MusicPlayerSettings;
